import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UsersIcon,
  UserGroupIcon,
  PhoneIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CalendarIcon,
  FunnelIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  TrophyIcon,
  FlagIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import { dashboardAPI } from '../services/api';

const UserAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // days
  const { user } = useAuth();

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      // For now, we'll use the user dashboard data and extend it
      // In a real implementation, you'd have a separate user analytics endpoint
      const response = await dashboardAPI.getUserDashboard();
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
      // Set empty data structure to prevent UI crashes
      setAnalyticsData({
        overview: {
          myLeads: 0,
          myCustomers: 0,
          myFollowups: 0,
          overdueFollowups: 0
        },
        leadsByStage: {},
        recentActivity: { leads: [], customers: [] }
      });
    } finally {
      setLoading(false);
    }
  };

  const formatChange = (value) => {
    const isPositive = value > 0;
    return (
      <div className={`flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? (
          <ArrowUpIcon className="h-4 w-4 mr-1" />
        ) : (
          <ArrowDownIcon className="h-4 w-4 mr-1" />
        )}
        <span className="text-sm font-medium">{Math.abs(value)}%</span>
      </div>
    );
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'lead':
        return UserGroupIcon;
      case 'customer':
        return UsersIcon;
      case 'followup':
        return PhoneIcon;
      default:
        return UserGroupIcon;
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - new Date(date)) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="large" text="Loading your analytics..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50 to-blue-50">
        <div className="p-6 max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 p-8 mb-8 shadow-2xl"
          >
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>

            <div className="relative z-10 flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                  <ChartBarIcon className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-white mb-2">
                    My Analytics
                  </h1>
                  <p className="text-indigo-100 text-lg">
                    Track your personal performance and progress
                  </p>
                </div>
              </div>

              {/* Date Range Selector */}
              <div className="flex items-center space-x-2">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                >
                  <option value="7" className="text-gray-900">Last 7 days</option>
                  <option value="30" className="text-gray-900">Last 30 days</option>
                  <option value="90" className="text-gray-900">Last 90 days</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Performance Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            {[
              {
                title: 'Total Leads',
                value: analyticsData?.overview?.myLeads || 0,
                icon: UserGroupIcon,
                color: 'from-blue-500 to-cyan-500',
                change: '+12%'
              },
              {
                title: 'Total Customers',
                value: analyticsData?.overview?.myCustomers || 0,
                icon: UsersIcon,
                color: 'from-green-500 to-emerald-500',
                change: '+8%'
              },
              {
                title: 'Follow-ups Completed',
                value: analyticsData?.overview?.myFollowups || 0,
                icon: CheckCircleIcon,
                color: 'from-purple-500 to-indigo-500',
                change: '+15%'
              },
              {
                title: 'Conversion Rate',
                value: analyticsData?.overview?.myLeads > 0 ?
                  Math.round((analyticsData?.overview?.myCustomers / analyticsData?.overview?.myLeads) * 100) : 0,
                icon: FlagIcon,
                color: 'from-orange-500 to-red-500',
                change: '+5%',
                suffix: '%'
              }
            ].map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {stat.value}{stat.suffix || ''}
                    </p>
                    <div className="flex items-center mt-2">
                      {formatChange(parseFloat(stat.change))}
                      <span className="text-sm text-gray-500 ml-2">vs last period</span>
                    </div>
                  </div>
                  <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Leads by Stage */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 px-6 py-4">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <FunnelIcon className="h-6 w-6 mr-2" />
                  My Leads by Stage
                </h3>
              </div>

              <div className="p-6">
                {analyticsData?.leadsByStage && analyticsData.leadsByStage.length > 0 ? (
                  <div className="space-y-4">
                    {analyticsData.leadsByStage.map((stageData, index) => {
                      const total = analyticsData.leadsByStage.reduce((a, b) => a + b.count, 0);
                      const percentage = total > 0 ? Math.round((stageData.count / total) * 100) : 0;

                      return (
                        <motion.div
                          key={stageData.name}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: 0.1 * index }}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="p-2 rounded-lg" style={{ backgroundColor: `${stageData.color}30` }}>
                              <UserGroupIcon className="h-5 w-5" style={{ color: stageData.color }} />
                            </div>
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900 capitalize">
                                {stageData.name}
                              </h4>
                              <p className="text-sm text-gray-600">{stageData.count} leads</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-gray-900">{percentage}%</div>
                            <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className="h-2 rounded-full"
                                style={{ width: `${percentage}%`, backgroundColor: stageData.color }}
                              ></div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FunnelIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No lead data available</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Recent Activity */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-green-500 to-blue-500 px-6 py-4">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <ClockIcon className="h-6 w-6 mr-2" />
                  Recent Activity
                </h3>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {[
                  ...(analyticsData?.recentActivity?.leads || []).map(lead => ({
                    id: lead._id,
                    type: 'lead',
                    title: `Updated lead: ${lead.firstName} ${lead.lastName}`,
                    time: formatTimeAgo(lead.updatedAt),
                    icon: UserGroupIcon,
                    color: 'bg-green-100 text-green-600'
                  })),
                  ...(analyticsData?.recentActivity?.customers || []).map(customer => ({
                    id: customer._id,
                    type: 'customer',
                    title: `Updated customer: ${customer.firstName} ${customer.lastName}`,
                    time: formatTimeAgo(customer.updatedAt),
                    icon: UsersIcon,
                    color: 'bg-blue-100 text-blue-600'
                  }))
                ]
                  .sort((a, b) => new Date(b.time) - new Date(a.time))
                  .slice(0, 8)
                  .map((activity, index) => (
                    <motion.div
                      key={`${activity.type}-${activity.id}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 * index }}
                      className="p-4 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`flex-shrink-0 p-2 rounded-lg ${activity.color}`}>
                          <activity.icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                          <p className="text-sm text-gray-500">{activity.time}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                {(!analyticsData?.recentActivity?.leads?.length && !analyticsData?.recentActivity?.customers?.length) && (
                  <div className="text-center py-8">
                    <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No recent activity</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Performance Goals */}
          {analyticsData?.activeGoals && analyticsData.activeGoals.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <FlagIcon className="h-6 w-6 mr-2" />
                  My Performance Goals
                </h3>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {analyticsData.activeGoals.map((goal, index) => {
                    const progress = (goal.currentValue / goal.targetValue) * 100;
                    const isCompleted = goal.status === 'completed';
                    const isExpiringSoon = goal.daysRemaining <= 3 && goal.daysRemaining > 0;

                    return (
                      <motion.div
                        key={goal._id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, delay: 0.1 * index }}
                        className="bg-gray-50 rounded-xl p-4 border border-gray-200"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-900 mb-1">{goal.title}</h4>
                            <p className="text-xs text-gray-500 capitalize">{goal.type}</p>
                          </div>
                          {isCompleted && (
                            <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                          )}
                          {isExpiringSoon && !isCompleted && (
                            <ExclamationTriangleIcon className="h-5 w-5 text-orange-500 flex-shrink-0" />
                          )}
                        </div>

                        <div className="mb-3">
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>{goal.currentValue}</span>
                            <span>{goal.targetValue}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${isCompleted ? 'bg-green-500' : progress >= 75 ? 'bg-blue-500' : progress >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${Math.min(progress, 100)}%` }}
                            ></div>
                          </div>
                          <div className="text-right text-sm font-medium text-gray-900 mt-1">
                            {progress.toFixed(1)}%
                          </div>
                        </div>

                        <div className="text-xs text-gray-500">
                          {goal.daysRemaining > 0 ? (
                            `${goal.daysRemaining} days left`
                          ) : goal.daysRemaining === 0 ? (
                            'Expires today'
                          ) : (
                            'Expired'
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default UserAnalytics;